// RacerView.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { db, auth } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast";
import { FaArrowLeft, FaTrophy, FaChartLine, FaPlus, FaChevronLeft, FaQuestionCircle, FaTimes } from "react-icons/fa";
import RacerModal from "./RacerModal";
import FilterDropdown from "./FilterDropdown";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../App.css";

// Register Chart.js components
Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

const generateDiceBearAvatar = (username) => {
  return `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(username)}`;
};

const RacerView = () => {
  const { racerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [racer, setRacer] = useState(null);
  const [events, setEvents] = useState([]);
  const [sessions, setSessions] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedTracks, setSelectedTracks] = useState(new Set());
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [showTrackFilter, setShowTrackFilter] = useState(false);
  const [showSessionFilter, setShowSessionFilter] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [chartMetric, setChartMetric] = useState("winning"); // "winning" or "median"
  const [pendingChartMetric, setPendingChartMetric] = useState(null);
  const [showMetricTooltip, setShowMetricTooltip] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupUsername, setSignupUsername] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const isGuest = Boolean(user?.isAnonymous);
  const isLoggedIn = Boolean(user && !user.isAnonymous);

  const trackParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("track");
    return value ? value.trim() : "";
  }, [location.search]);

  useEffect(() => {
    if (!trackParam) return;
    setSelectedTracks(new Set([trackParam]));
  }, [trackParam]);

  // Fetch racer data
  useEffect(() => {
    const fetchRacer = async () => {
      if (!racerId) {
        setLoading(false);
        return;
      }

      try {
        const racerDoc = await getDoc(doc(db, "timer_racers", racerId));
        if (racerDoc.exists()) {
          setRacer({ id: racerDoc.id, ...racerDoc.data() });
        } else {
          showToast("Racer not found.");
          navigate("/racers-list");
        }
      } catch (error) {
        console.error("Error fetching racer:", error);
        showToast("Failed to load racer.");
        setLoading(false);
      }
    };

    fetchRacer();
  }, [racerId, navigate, showToast]);

  // Fetch all events
  useEffect(() => {
    const fetchEvents = async () => {
      if (!racerId) {
        setLoading(false);
        return;
      }

      try {
        const eventsRef = collection(db, "events");
        const eventsSnapshot = await getDocs(eventsRef);
        const allEvents = eventsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter events where this racer participated
        const relevantEvents = allEvents.filter((event) => {
          if (!event.racers || !Array.isArray(event.racers)) return false;
          return event.racers.some((r) => r.racerId === racerId);
        });

        setEvents(relevantEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
        showToast("Failed to load events.");
      } finally {
        setLoading(false);
      }
    };

    if (racer) {
      fetchEvents();
    }
  }, [racerId, racer, showToast]);

  // Fetch sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const sessionsRef = collection(db, "sessions");
        const sessionsSnapshot = await getDocs(sessionsRef);
        const sessionsMap = new Map();
        sessionsSnapshot.docs.forEach((docSnap) => {
          sessionsMap.set(docSnap.id, {
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
        setSessions(sessionsMap);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };

    fetchSessions();
  }, []);

  // Get distinct tracks from events
  const availableTracks = useMemo(() => {
    const tracks = new Set();
    events.forEach((event) => {
      if (event.track && event.track.trim()) {
        tracks.add(event.track.trim());
      }
    });
    return Array.from(tracks).sort();
  }, [events]);

  // Get distinct sessions from events
  const availableSessions = useMemo(() => {
    const sessionIds = new Set();
    events.forEach((event) => {
      const eventSessionIds = event.sessionIds || (event.sessionId ? [event.sessionId] : []);
      eventSessionIds.forEach((id) => {
        if (id && sessions.has(id)) {
          sessionIds.add(id);
        }
      });
    });
    return Array.from(sessionIds)
      .map((id) => sessions.get(id))
      .filter(Boolean)
      .sort((a, b) => {
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB);
      });
  }, [events, sessions]);

  const compareRacerIds = useMemo(() => {
    const racerIds = new Set();
    events.forEach((event) => {
      if (!event.racers || !Array.isArray(event.racers)) return;
      event.racers.forEach((racerEntry) => {
        if (racerEntry?.racerId) {
          racerIds.add(racerEntry.racerId);
        }
      });
    });
    return Array.from(racerIds);
  }, [events]);

  const allowedRacerIds = useMemo(() => {
    if (isGuest || !user) {
      return compareRacerIds;
    }
    return [];
  }, [compareRacerIds, isGuest, user]);

  // Calculate racer statistics
  const racerStats = useMemo(() => {
    if (!racer || events.length === 0) {
      return {
        totalEvents: 0,
        finishedEvents: 0,
        wins: 0,
        averagePosition: 0,
        bestPosition: null,
        averageTime: 0,
        bestTime: null,
        totalTime: 0,
        totalPosition: 0,
        results: [],
        chartData: null,
      };
    }

    const results = [];
    let totalEvents = 0;
    let finishedEvents = 0;
    let wins = 0;
    let totalPosition = 0;
    let totalTime = 0;
    let bestPosition = null;
    let bestTime = null;
    const chartDataPoints = [];

    // Filter events by selected tracks and sessions
    const filteredEvents = events.filter((event) => {
      // If no filters selected, show all
      if (selectedTracks.size === 0 && selectedSessions.size === 0) return true;
      
      // Check track filter
      const matchesTrack = selectedTracks.size === 0 || 
        (event.track && selectedTracks.has(event.track.trim()));
      
      // Check session filter
      const eventSessionIds = event.sessionIds || (event.sessionId ? [event.sessionId] : []);
      const matchesSession = selectedSessions.size === 0 || 
        eventSessionIds.some((id) => selectedSessions.has(id));
      
      // Event must match both selected filters (if any)
      return matchesTrack && matchesSession;
    });

    filteredEvents.forEach((event) => {
      if (!event.racers || !Array.isArray(event.racers)) return;

      const racerEntry = event.racers.find((r) => r.racerId === racerId);
      if (!racerEntry) return;

      totalEvents++;

      // Get finished racers for this event
      const finishedRacers = event.racers
        .filter((r) => r.duration !== null && r.duration !== undefined)
        .sort((a, b) => a.duration - b.duration);

      if (racerEntry.duration !== null && racerEntry.duration !== undefined) {
        finishedEvents++;
        const position = finishedRacers.findIndex((r) => r.racerId === racerId) + 1;
        const winningTime = finishedRacers.length > 0 ? finishedRacers[0].duration : null;
        
        // Calculate median time
        let medianTime = null;
        if (finishedRacers.length > 0) {
          const sortedDurations = finishedRacers.map(r => r.duration).sort((a, b) => a - b);
          const mid = Math.floor(sortedDurations.length / 2);
          medianTime = sortedDurations.length % 2 === 0
            ? (sortedDurations[mid - 1] + sortedDurations[mid]) / 2
            : sortedDurations[mid];
        }
        
        const percentFromWinner = winningTime
          ? ((racerEntry.duration - winningTime) / winningTime) * 100
          : 0;
        const percentFromMedian = medianTime
          ? ((racerEntry.duration - medianTime) / medianTime) * 100
          : 0;

        totalPosition += position;
        totalTime += racerEntry.duration;

        if (bestPosition === null || position < bestPosition) {
          bestPosition = position;
        }
        if (bestTime === null || racerEntry.duration < bestTime) {
          bestTime = racerEntry.duration;
        }

        if (position === 1) {
          wins++;
        }

        const eventDate = event.createdAt?.seconds
          ? new Date(event.createdAt.seconds * 1000)
          : event.createdAt
          ? new Date(event.createdAt)
          : null;

        const eventSessionIds = event.sessionIds || (event.sessionId ? [event.sessionId] : []);
        const sessionNames = eventSessionIds
          .map((id) => sessions.get(id))
          .filter(Boolean)
          .map((session) => session.name || "")
          .filter((name) => name.trim() !== "");

        results.push({
          eventId: event.id,
          eventName: event.name,
          trackName: event.track || event.trackName || "",
          sessionNames,
          date: event.createdAt,
          eventDate,
          duration: racerEntry.duration,
          position,
          totalRacers: finishedRacers.length,
          percentFromWinner,
          percentFromMedian,
        });

        // Store both metrics for chart
        if (eventDate) {
          chartDataPoints.push({
            x: eventDate,
            y: percentFromWinner,
            yMedian: percentFromMedian,
          });
        }
      }
    });

    // Sort results by date (most recent first)
    results.sort((a, b) => {
      const aDate = a.date?.seconds || a.date || 0;
      const bDate = b.date?.seconds || b.date || 0;
      return bDate - aDate;
    });

    // Prepare chart data
    const sortedResults = [...results].sort((a, b) => {
      const aDate = a.eventDate?.getTime() || 0;
      const bDate = b.eventDate?.getTime() || 0;
      return aDate - bDate;
    });

    const chartLabels = sortedResults.map((result) => {
      if (!result.eventDate) return "";
      return result.eventDate.toLocaleDateString();
    });

    const chartData = chartLabels.length > 0 ? {
      labels: chartLabels,
      datasets: [
        {
          label: chartMetric === "winning" 
            ? "% Difference from Winning Time"
            : "% Time Over Median Time",
          data: sortedResults.map((r) => 
            chartMetric === "winning" ? r.percentFromWinner : r.percentFromMedian
          ),
          borderColor: "rgb(19, 161, 168)",
          backgroundColor: "rgb(19, 161, 168)20",
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    } : null;

    return {
      totalEvents,
      finishedEvents,
      wins,
      averagePosition: finishedEvents > 0 ? totalPosition / finishedEvents : 0,
      bestPosition,
      averageTime: finishedEvents > 0 ? totalTime / finishedEvents : 0,
      bestTime,
      results,
      chartData,
    };
  }, [racer, events, racerId, selectedTracks, selectedSessions, chartMetric]);

  const formatDuration = (ms) => {
    if (!ms) return "-";
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, "0")}.${milliseconds.toString().padStart(2, "0")}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const handleToggleTrack = (track) => {
    setSelectedTracks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(track)) {
        newSet.delete(track);
      } else {
        newSet.add(track);
      }
      return newSet;
    });
  };

  const handleToggleSession = (sessionId) => {
    setSelectedSessions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleOpenCompareModal = () => {
    setShowCompareModal(true);
  };

  const handleCloseCompareModal = () => {
    setShowCompareModal(false);
  };

  const handleClaimRacer = () => {
    if (isLoggedIn) {
      showToast("You are already logged in. You can view this racer's results in your account.");
      return;
    }
    setShowSignupModal(true);
  };

  const handleSignup = async () => {
    if (!signupEmail || !signupPassword || !signupUsername) {
      showToast("Please fill in all fields");
      return;
    }

    if (signupPassword.length < 6) {
      showToast("Password must be at least 6 characters");
      return;
    }

    setSignupLoading(true);
    try {
      // Create the user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupEmail,
        signupPassword
      );
      const newUser = userCredential.user;

      // Create the document in Firestore with accepted=true and initial credits
      await setDoc(doc(db, "users", newUser.uid), {
        email: signupEmail,
        username: signupUsername,
        accepted: true,
        credits: 100,
        origin: "chronocult.com",
      });

      // Create share group with resources structure
      const sharegroupData = {
        ownerId: newUser.uid,
        name: "public",
        createdAt: Date.now(),
        resources: {
          runs: [],
          racers: racerId ? [racerId] : [],
          groups: []
        }
      };

      await setDoc(doc(db, "sharegroups", newUser.uid), sharegroupData);

      // If racerId is provided, also update the racer document to include this sharegroup
      if (racerId) {
        try {
          await updateDoc(doc(db, "timer_racers", racerId), {
            sharingGroups: arrayUnion(newUser.uid)
          });
        } catch (error) {
          console.error("Error updating racer with sharegroup:", error);
          // Don't throw - user creation succeeded, this is just a bonus
        }
      }

      showToast("Account created! You can now follow this racer's results and receive notifications when new results are posted.");
      setShowSignupModal(false);
      setSignupEmail("");
      setSignupPassword("");
      setSignupUsername("");
      
      // Reload the page to show the user as logged in
      window.location.reload();
    } catch (error) {
      console.error("Error creating account:", error);
      showToast(error.message || "Failed to create account");
    } finally {
      setSignupLoading(false);
    }
  };

  const handleConfirmCompareSelection = (selectedRacers) => {
    const selectedIds = new Set(selectedRacers.map((selectedRacer) => selectedRacer.id));
    if (racerId) {
      selectedIds.add(racerId);
    }
    if (selectedIds.size < 2) {
      showToast("Please select at least 2 racers to compare.");
      return;
    }
    navigate(`/compare/${Array.from(selectedIds).join(",")}`);
    setShowCompareModal(false);
  };

  // Apply pending chart metric after a delay to allow dropdown to finish
  useEffect(() => {
    if (pendingChartMetric !== null) {
      const timer = setTimeout(() => {
        setChartMetric(pendingChartMetric);
        setPendingChartMetric(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingChartMetric]);

  // Chart component - memoized to prevent re-renders when parent state changes
  const ChartComponent = React.memo(({ chartData, chartMetric }) => {
    const chartCanvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
      if (!chartCanvasRef.current || !chartData) return;

      // Destroy existing chart if it exists
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }

      const ctx = chartCanvasRef.current.getContext("2d");
      chartInstanceRef.current = new Chart(ctx, {
        type: "line",
        data: chartData,
          options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: true,
              text: chartMetric === "winning"
                ? "% Difference from Winning Time Over Time"
                : "% Time Over Median Time Over Time",
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  if (context.parsed.y === null) return "";
                  return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: chartMetric === "winning"
                  ? "% Difference from Winner"
                  : "% Over Median Time",
              },
              ticks: {
                callback: function (value) {
                  return value.toFixed(1) + "%";
                },
              },
            },
            x: {
              title: {
                display: true,
                text: "Event Date",
              },
            },
          },
        },
      });

      return () => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
      };
    }, [chartData, chartMetric]);

    return (
      <div style={{ position: "relative", height: "400px", width: "100%", marginBottom: "30px" }}>
        <canvas ref={chartCanvasRef} style={{ display: "block", maxWidth: "100%", maxHeight: "100%" }}></canvas>
      </div>
    );
  });

  if (loading) {
    return (
      <div style={{ width: "100%", padding: "30px 20px", textAlign: "center", boxSizing: "border-box", maxWidth: "100%" }}>
        <p>Loading racer data...</p>
      </div>
    );
  }

  if (!racer) {
    return (
      <div style={{ width: "100%", padding: "30px 20px", textAlign: "center", boxSizing: "border-box", maxWidth: "100%" }}>
        <p>Racer not found.</p>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", padding: "30px 20px", boxSizing: "border-box", maxWidth: "100%" }}>
      <div className="homepage-other-tools">
        {/* Header */}
        <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
          <button
            onClick={() => navigate("/racers-list")}
            style={{
              padding: "8px",
              backgroundColor: "transparent",
              border: "none",
              color: "#000",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaChevronLeft size={20} />
          </button>
          <h1 style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, flexWrap: "wrap" }}>
            <img 
              src={generateDiceBearAvatar(racer.name)} 
              alt="Racer Avatar" 
              style={{
                width: "35px",
                height: "35px",
                borderRadius: "0%",
                objectFit: "cover"
              }}
            />
            <span style={{ marginRight: "10px" }}>{racer.name}</span>
            {!isLoggedIn && (
              <span style={{ display: "flex", alignItems: "center", gap: "5px", position: "relative", marginLeft: "auto" }}>
                <button
                  onClick={handleClaimRacer}
                  style={{
                    padding: "4px 8px",
                    backgroundColor: "transparent",
                    border: "none",
                    color: "#13A1A8",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    textDecoration: "underline",
                  }}
                >
                  claim this racer
                </button>
                <button
                  onClick={() => setShowInfoTooltip(!showInfoTooltip)}
                  onMouseEnter={() => setShowInfoTooltip(true)}
                  onMouseLeave={() => setShowInfoTooltip(false)}
                  style={{
                    padding: "4px",
                    backgroundColor: "transparent",
                    border: "none",
                    color: "#2196F3",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FaQuestionCircle size={14} />
                </button>
                {showInfoTooltip && (
                  <div
                    style={{
                      position: "absolute",
                      top: "24px",
                      right: "0",
                      backgroundColor: "#212121",
                      color: "#fff",
                      padding: "10px",
                      borderRadius: "5px",
                      fontSize: "12px",
                      maxWidth: "280px",
                      zIndex: 1000,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                    onMouseEnter={() => setShowInfoTooltip(true)}
                    onMouseLeave={() => setShowInfoTooltip(false)}
                  >
                    Create an account and you'll be able to follow this racer's results and get notifications when new results are posted.
                  </div>
                )}
              </span>
            )}
          </h1>
          <button
            onClick={handleOpenCompareModal}
            style={{
              padding: "8px 15px",
              backgroundColor: "#13A1A8",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              borderRadius: "3px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <FaPlus /> Compare with Others
          </button>
        </div>

        {/* Track and Session Filters */}
        <div style={{ marginBottom: "20px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {/* Track Filter */}
          {availableTracks.length > 0 && (
            <div style={{ flex: "1", minWidth: "200px" }}>
              <FilterDropdown
                label="Filter by Track:"
                title="Filter by track"
                dropdownTitle="Filter Tracks"
                searchPlaceholder="Search tracks..."
                emptyText="No tracks found"
                options={availableTracks}
                selectedValues={Array.from(selectedTracks)}
                onToggle={handleToggleTrack}
                onClear={() => setSelectedTracks(new Set())}
                buttonText="Filter"
                activeBackgroundColor="#6c757d"
                inactiveTextColor="#333"
                isOpen={showTrackFilter}
                onOpenChange={setShowTrackFilter}
                enableSearch
              />
            </div>
          )}

          {/* Session Filter */}
          {availableSessions.length > 0 && (
            <div style={{ flex: "1", minWidth: "200px" }}>
              <FilterDropdown
                label="Filter by Session:"
                title="Filter by session"
                dropdownTitle="Filter Sessions"
                searchPlaceholder="Search sessions..."
                emptyText="No sessions found"
                options={availableSessions}
                selectedValues={Array.from(selectedSessions)}
                getOptionValue={(session) => session.id}
                getOptionLabel={(session) => session.name || `Session ${session.id}`}
                onToggle={handleToggleSession}
                onClear={() => setSelectedSessions(new Set())}
                buttonText="Filter"
                activeBackgroundColor="#6c757d"
                inactiveTextColor="#333"
                isOpen={showSessionFilter}
                onOpenChange={setShowSessionFilter}
                enableSearch
              />
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {racerStats.results.length > 0 && (
          <div style={{ marginBottom: "30px" }}>
            <h2 style={{ marginBottom: "15px" }}>Summary Statistics</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #13A1A8",
                }}
              >
                <div style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px" }}>
                  Total Runs
                </div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {racerStats.totalEvents}
                </div>
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #13A1A8",
                }}
              >
                <div style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px" }}>
                  Finished Runs
                </div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {racerStats.finishedEvents}
                </div>
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#fff3cd",
                  borderRadius: "5px",
                  border: "1px solid #ffc107",
                }}
              >
                <div style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px" }}>
                  <FaTrophy style={{ marginRight: "5px" }} />
                  Wins
                </div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {racerStats.wins}
                </div>
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #13A1A8",
                }}
              >
                <div style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px" }}>
                  Average Position
                </div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {racerStats.finishedEvents > 0
                    ? racerStats.averagePosition.toFixed(1)
                    : "-"}
                </div>
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #13A1A8",
                }}
              >
                <div style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px" }}>
                  Best Position
                </div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {racerStats.bestPosition || "-"}
                </div>
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #13A1A8",
                }}
              >
                <div style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px" }}>
                  Best Time
                </div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {formatDuration(racerStats.bestTime)}
                </div>
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #13A1A8",
                }}
              >
                <div style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px" }}>
                  Average Time
                </div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {formatDuration(racerStats.averageTime)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chart */}
        {racerStats.chartData && racerStats.results.length > 0 && (
          <div style={{ marginBottom: "40px" }}>
            <div style={{ marginBottom: "15px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
              <h2 style={{ margin: 0 }}>
                <FaChartLine style={{ marginRight: "8px" }} />
                Performance Chart
              </h2>
              <div 
                style={{ display: "flex", alignItems: "center", gap: "10px", position: "relative" }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <label style={{ fontSize: "0.9em", fontWeight: "500" }}>Metric:</label>
                <select
                  value={pendingChartMetric !== null ? pendingChartMetric : chartMetric}
                  onChange={(e) => {
                    e.stopPropagation();
                    const newValue = e.target.value;
                    // Set pending value immediately for UI feedback, apply to chart after delay
                    setPendingChartMetric(newValue);
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    e.stopPropagation();
                    // Apply immediately on blur if there's a pending value
                    if (pendingChartMetric !== null) {
                      setChartMetric(pendingChartMetric);
                      setPendingChartMetric(null);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontSize: "0.9em",
                    zIndex: 1000,
                    position: "relative",
                    isolation: "isolate",
                  }}
                >
                  <option value="winning">% Difference from Winning Time</option>
                  <option value="median">% Time Over Median Time</option>
                </select>
                <div style={{ position: "relative" }}>
                  <FaQuestionCircle
                    style={{
                      cursor: "pointer",
                      color: "#666",
                      fontSize: "1.1em",
                    }}
                    onMouseEnter={() => setShowMetricTooltip(true)}
                    onMouseLeave={() => setShowMetricTooltip(false)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMetricTooltip(!showMetricTooltip);
                    }}
                  />
                  {showMetricTooltip && (
                    <div
                      style={{
                        position: "absolute",
                        top: "30px",
                        right: "0",
                        width: "320px",
                        padding: "15px",
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 10000,
                        fontSize: "0.9em",
                        lineHeight: "1.6",
                      }}
                      onMouseEnter={() => setShowMetricTooltip(true)}
                      onMouseLeave={() => setShowMetricTooltip(false)}
                    >
                      <div style={{ marginBottom: "12px" }}>
                        <strong style={{ color: "#13A1A8", display: "block", marginBottom: "6px" }}>
                          % Difference from Winning Time
                        </strong>
                        <div style={{ color: "#333", marginBottom: "8px" }}>
                          Shows how much slower (or faster) you were compared to the fastest time in each event.
                        </div>
                        <div style={{ color: "#28a745", fontWeight: "500" }}>
                          ✓ Lower is better (smaller values mean you're closer to the winning time)
                        </div>
                      </div>
                      <div style={{ borderTop: "1px solid #eee", paddingTop: "12px" }}>
                        <strong style={{ color: "#13A1A8", display: "block", marginBottom: "6px" }}>
                          % Time Over Median Time
                        </strong>
                        <div style={{ color: "#333", marginBottom: "8px" }}>
                          Shows how your time compares to the middle (median) time of all racers who finished.
                        </div>
                        <div style={{ color: "#28a745", fontWeight: "500" }}>
                          ✓ Lower is better (shows you performed better than average)
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ 
              padding: "20px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              backgroundColor: "#fff",
              marginBottom: "30px"
            }}>
              <ChartComponent chartData={racerStats.chartData} chartMetric={chartMetric} />
            </div>
          </div>
        )}

        {/* Event Results Table */}
        {racerStats.results.length > 0 ? (
          <div style={{ marginTop: "20px" }}>
            <h2 style={{ marginBottom: "15px" }}>Run Results</h2>
            <div
              style={{
                maxHeight: "600px",
                overflowY: "auto",
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #ccc" }}>
                    <th style={{ padding: "10px", textAlign: "left" }}>Event</th>
                    <th style={{ padding: "10px", textAlign: "left" }}>Track</th>
                    <th style={{ padding: "10px", textAlign: "left" }}>Session</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Date</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Position</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Time</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>% from Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {racerStats.results.map((result) => (
                    <tr
                      key={result.eventId}
                      style={{
                        borderBottom: "1px solid #eee",
                        backgroundColor: result.position === 1 ? "#fff3cd" : "transparent",
                      }}
                    >
                      <td style={{ padding: "10px" }}>{result.eventName}</td>
                      <td style={{ padding: "10px" }}>{result.trackName || "—"}</td>
                      <td style={{ padding: "10px" }}>
                        {result.sessionNames.length > 0 ? result.sessionNames.join(", ") : "—"}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {formatDate(result.date)}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {result.position === 1 ? (
                          <span style={{ fontWeight: "bold", color: "#ffc107" }}>
                            <FaTrophy style={{ marginRight: "5px" }} />
                            {result.position}/{result.totalRacers}
                          </span>
                        ) : (
                          `${result.position}/${result.totalRacers}`
                        )}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {formatDuration(result.duration)}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {result.percentFromWinner.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            <p>No event results found for this racer.</p>
          </div>
        )}
      </div>

      {showCompareModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <RacerModal
              eventOwnerId={user?.uid || racer?.createdBy}
              allowedRacerIds={allowedRacerIds}
              eventRacers={racerId ? [{ racerId }] : []}
              onClose={handleCloseCompareModal}
              onSelectRacers={handleConfirmCompareSelection}
            />
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0 }}>Create Account</h2>
              <button
                onClick={() => setShowSignupModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "20px",
                  color: "#666",
                }}
              >
                <FaTimes />
              </button>
            </div>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              Create an account to follow this racer's results and get notifications when new results are posted.
            </p>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Username</label>
              <input
                type="text"
                value={signupUsername}
                onChange={(e) => setSignupUsername(e.target.value)}
                placeholder="Choose a username"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Email</label>
              <input
                type="email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                placeholder="Enter your email"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Password</label>
              <input
                type="password"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                placeholder="Create a password (min 6 characters)"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <button
              onClick={handleSignup}
              disabled={signupLoading}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: signupLoading ? "#ccc" : "#13A1A8",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: signupLoading ? "not-allowed" : "pointer",
              }}
            >
              {signupLoading ? "Creating Account..." : "Create Account"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RacerView;
